import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, User, Scissors, Scale, Droplets, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const CirugiaCorporal = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Corporal',
      title1: 'Esculpir el cuerpo,',
      title2: 'respetar la anatomía',
      description: 'La cirugía corporal permite corregir volúmenes, eliminar excesos y restablecer proporciones. Siempre con respeto a la anatomía individual y buscando resultados naturales.',
      moreInfo: 'Más información',
      ctaEyebrow: 'Consulta Personalizada',
      ctaTitle: '¿Qué procedimiento es adecuado para usted?',
      ctaDesc: 'En una consulta de valoración analizaremos sus objetivos y le propondremos el plan más adecuado.',
      ctaButton: 'Solicitar valoración',
    },
    en: {
      eyebrow: 'Body Surgery',
      title1: 'Sculpt the body,',
      title2: 'respect anatomy',
      description: 'Body surgery allows correction of volumes, removal of excess and restoration of proportions. Always respecting individual anatomy and seeking natural results.',
      moreInfo: 'More information',
      ctaEyebrow: 'Personalized Consultation',
      ctaTitle: 'Which procedure is right for you?',
      ctaDesc: 'In an assessment consultation we will analyze your goals and propose the most suitable plan.',
      ctaButton: 'Request assessment',
    },
    fr: {
      eyebrow: 'Chirurgie Corporelle',
      title1: 'Sculpter le corps,',
      title2: 'respecter l\'anatomie',
      description: 'La chirurgie corporelle permet de corriger les volumes, d\'éliminer les excès et de rétablir les proportions. Toujours dans le respect de l\'anatomie individuelle et en recherchant des résultats naturels.',
      moreInfo: 'Plus d\'informations',
      ctaEyebrow: 'Consultation Personnalisée',
      ctaTitle: 'Quelle procédure vous convient?',
      ctaDesc: 'Lors d\'une consultation d\'évaluation, nous analyserons vos objectifs et vous proposerons le plan le plus adapté.',
      ctaButton: 'Demander une évaluation',
    },
    pt: {
      eyebrow: 'Cirurgia Corporal',
      title1: 'Esculpir o corpo,',
      title2: 'respeitar a anatomia',
      description: 'A cirurgia corporal permite corrigir volumes, eliminar excessos e restabelecer proporções. Sempre respeitando a anatomia individual e buscando resultados naturais.',
      moreInfo: 'Mais informações',
      ctaEyebrow: 'Consulta Personalizada',
      ctaTitle: 'Qual procedimento é adequado para você?',
      ctaDesc: 'Em uma consulta de avaliação analisaremos seus objetivos e proporemos o plano mais adequado.',
      ctaButton: 'Solicitar avaliação',
    },
  };

  const procedimientos = {
    es: [
      { titulo: 'Cirugía de Mamas', icono: Heart, descripcion: 'Aumento, reducción, mastopexia. Técnicas adaptadas a cada anatomía y deseo estético.', url: '/cirugia-mamas' },
      { titulo: 'Ginecomastia', icono: User, descripcion: 'Reducción del tejido mamario masculino. Resultados definitivos en una sola intervención.', url: '/ginecomastia' },
      { titulo: 'Abdominoplastia', icono: Scissors, descripcion: 'Resección del exceso cutáneo abdominal. Reparación de diástasis muscular si existe.', url: '/abdominoplastia' },
      { titulo: 'Cirugía Postbariátrica', icono: Scale, descripcion: 'Plan quirúrgico integral tras pérdida masiva de peso. Intervenciones secuenciales según necesidad.', url: '/cirugia-postbariatrica' },
      { titulo: 'Liposucción', icono: Droplets, descripcion: 'Redefinición del contorno corporal con técnica láser y tumescente.', url: '/liposuccion' },
      { titulo: 'Ginecología Estética', icono: Users, descripcion: 'Rejuvenecimiento íntimo femenino. Dra. Susana Varela.', url: '/gineco-estetica' },
    ],
    en: [
      { titulo: 'Breast Surgery', icono: Heart, descripcion: 'Augmentation, reduction, mastopexy. Techniques adapted to each anatomy and aesthetic desire.', url: '/cirugia-mamas' },
      { titulo: 'Gynecomastia', icono: User, descripcion: 'Male breast tissue reduction. Definitive results in a single procedure.', url: '/ginecomastia' },
      { titulo: 'Abdominoplasty', icono: Scissors, descripcion: 'Resection of abdominal skin excess. Muscle diastasis repair if present.', url: '/abdominoplastia' },
      { titulo: 'Post-Bariatric Surgery', icono: Scale, descripcion: 'Comprehensive surgical plan after massive weight loss. Sequential procedures as needed.', url: '/cirugia-postbariatrica' },
      { titulo: 'Liposuction', icono: Droplets, descripcion: 'Body contour redefinition with laser and tumescent technique.', url: '/liposuccion' },
      { titulo: 'Gynecological Aesthetics', icono: Users, descripcion: 'Female intimate rejuvenation. Dr. Susana Varela.', url: '/gineco-estetica' },
    ],
    fr: [
      { titulo: 'Chirurgie Mammaire', icono: Heart, descripcion: 'Augmentation, réduction, mastopexie. Techniques adaptées à chaque anatomie et désir esthétique.', url: '/cirugia-mamas' },
      { titulo: 'Gynécomastie', icono: User, descripcion: 'Réduction du tissu mammaire masculin. Résultats définitifs en une seule intervention.', url: '/ginecomastia' },
      { titulo: 'Abdominoplastie', icono: Scissors, descripcion: 'Résection de l\'excès cutané abdominal. Réparation du diastasis musculaire si présent.', url: '/abdominoplastia' },
      { titulo: 'Chirurgie Post-Bariatrique', icono: Scale, descripcion: 'Plan chirurgical complet après perte de poids massive. Interventions séquentielles selon les besoins.', url: '/cirugia-postbariatrica' },
      { titulo: 'Liposuccion', icono: Droplets, descripcion: 'Redéfinition du contour corporel avec technique laser et tumescente.', url: '/liposuccion' },
      { titulo: 'Esthétique Gynécologique', icono: Users, descripcion: 'Rajeunissement intime féminin. Dr. Susana Varela.', url: '/gineco-estetica' },
    ],
    pt: [
      { titulo: 'Cirurgia de Mamas', icono: Heart, descripcion: 'Aumento, redução, mastopexia. Técnicas adaptadas a cada anatomia e desejo estético.', url: '/cirugia-mamas' },
      { titulo: 'Ginecomastia', icono: User, descripcion: 'Redução do tecido mamário masculino. Resultados definitivos em uma única intervenção.', url: '/ginecomastia' },
      { titulo: 'Abdominoplastia', icono: Scissors, descripcion: 'Ressecção do excesso cutâneo abdominal. Reparação da diástase muscular se existir.', url: '/abdominoplastia' },
      { titulo: 'Cirurgia Pós-Bariátrica', icono: Scale, descripcion: 'Plano cirúrgico integral após perda massiva de peso. Intervenções sequenciais conforme necessidade.', url: '/cirugia-postbariatrica' },
      { titulo: 'Lipoaspiração', icono: Droplets, descripcion: 'Redefinição do contorno corporal com técnica laser e tumescente.', url: '/liposuccion' },
      { titulo: 'Ginecologia Estética', icono: Users, descripcion: 'Rejuvenescimento íntimo feminino. Dra. Susana Varela.', url: '/gineco-estetica' },
    ],
  };

  const t = texts[lang] || texts.es;
  const procs = procedimientos[lang] || procedimientos.es;

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      <section className="pt-12 pb-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow mb-6">{t.eyebrow}</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6">
              {t.title1}<br /><span className="text-gold">{t.title2}</span>
            </h1>
            <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">{t.description}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid gap-8">
            {procs.map((proc, index) => {
              const Icon = proc.icono;
              return (
                <AnimatedSection key={index} animation="fadeUp" delay={index * 0.1}>
                  <Link to={proc.url} className="group card-luxury p-8 flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-16 h-16 border border-gold flex items-center justify-center flex-shrink-0 group-hover:bg-gold transition-colors">
                      <Icon className="text-gold group-hover:text-charcoal transition-colors" size={24} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-serif text-2xl text-charcoal mb-3 group-hover:text-gold transition-colors">{proc.titulo}</h2>
                      <p className="text-text-secondary leading-relaxed mb-4">{proc.descripcion}</p>
                      <span className="inline-flex items-center gap-2 text-gold text-sm tracking-wider uppercase group-hover:gap-3 transition-all">
                        {t.moreInfo} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow text-gold mb-6">{t.ctaEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">{t.ctaTitle}</h2>
            <p className="text-gold-light/70 mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="btn-gold inline-flex items-center gap-2 group">
              {t.ctaButton}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CirugiaCorporal;
